import { Button, Heading, Input, Separator, Spinner, Text } from "@overmap-ai/blocks"
import { ERROR_MSG_UNEXPECTED } from "@overmap-ai/common"
import { APIError } from "@overmap-ai/core"
import { useForm } from "@tanstack/react-form"
import { type FormEventHandler, memo, useCallback } from "react"

import { openLogin, sdk } from "../../sdk"

interface ApplicationInviteFormProps {
	code: string | undefined
}

export const ApplicationInviteForm = memo<ApplicationInviteFormProps>((props) => {
	const { code } = props

	const form = useForm({
		defaultValues: {
			username: "",
			password: "",
		},
		onSubmit: async ({ value, formApi }) => {
			try {
				await sdk.emailVerifications.validateVerificationCode(code!, {
					username: value.username,
					email: value.username,
					password: value.password,
				})

				openLogin("/")

				formApi.reset()
			} catch (error) {
				formApi.setErrorMap({
					onSubmit: {
						form: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
						fields: {},
					},
				})
			}
		},
	})

	const handleSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
		(e) => {
			e.preventDefault()
			e.stopPropagation()
			void form.handleSubmit()
		},
		[form],
	)

	return (
		<section className="flex flex-col items-center gap-4 px-6 py-4">
			<Heading size="xl">Sign up</Heading>

			<Separator size="full" />

			<form className="flex w-full flex-col gap-2" onSubmit={handleSubmit}>
				<form.Field
					name="username"
					validators={{
						onBlur: ({ value }) => (!value ? "Username is required" : undefined),
					}}
				>
					{(field) => (
						<div className="flex w-full flex-col gap-1">
							<label htmlFor="username">
								<Text>Username</Text>
							</label>

							<Input.Root className="!bg-(--base-a2) w-full" variant="outline">
								<Input.Field
									id={field.name}
									name={field.name}
									value={field.state.value}
									onBlur={field.handleBlur}
									onChange={(e) => field.handleChange(e.target.value)}
								/>
							</Input.Root>
							{!field.state.meta.isValid && (
								<Text size="sm" weight="regular" accentColor="danger">
									{field.state.meta.errors.join(",")}
								</Text>
							)}
						</div>
					)}
				</form.Field>

				<form.Field
					name="password"
					validators={{
						onBlur: ({ value }) => (!value ? "Password is required" : undefined),
					}}
				>
					{(field) => (
						<div className="flex w-full flex-col gap-1">
							<label htmlFor="password">
								<Text>Password</Text>
							</label>

							<Input.Root className="!bg-(--base-a2) w-full" variant="outline">
								<Input.Field
									className=""
									type="password"
									id={field.name}
									name={field.name}
									value={field.state.value}
									onBlur={field.handleBlur}
									onChange={(e) => field.handleChange(e.target.value)}
								/>
							</Input.Root>
							{!field.state.meta.isValid && (
								<Text size="sm" weight="regular" accentColor="danger">
									{field.state.meta.errors.join(",")}
								</Text>
							)}
						</div>
					)}
				</form.Field>

				<form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
					{([canSubmit, isSubmitting]) => (
						<Button className="my-2 w-full" type="submit" disabled={!canSubmit} variant="surface">
							{isSubmitting ? <Spinner /> : "Create account"}
						</Button>
					)}
				</form.Subscribe>

				<form.Subscribe selector={(state) => [state.errorMap]}>
					{([errorMap]) =>
						!!errorMap.onSubmit && (
							<Text accentColor="danger" size="xs">
								{errorMap.onSubmit}
							</Text>
						)
					}
				</form.Subscribe>
			</form>
		</section>
	)
})
ApplicationInviteForm.displayName = "ApplicationInviteForm"
